"use client";

import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import CloseCircle from "@/assets/svg/close-circle.svg";
import { useImagePreloader } from "@/hooks/use-image-preloader";
import { cn } from "@/lib/utils";
import { getCurrentAreaInClient } from "@/lib/utils.client";
import { CMSImage, CMSLink } from "@/modules/cms/components/cms-items";
import { useORPC } from "@/providers/orpc-provider";
import { useTracking } from "@/track";
import { getImageUrlByDevice } from "../utils";
import { useUserState } from "./advertisement-wrapper";
import { advertisementRecordsAtom } from "./store/advertisement-atoms";
import { type AdvertisementConfig, canShowBasedOnFrequency, generateAdvertisementContentHash, shouldShowAdvertisementComplete } from "./utils/advertisement-helpers";
export function AdvertisementNewcomer() {
  const $ = _c(73);
  const [isOpen, setIsOpen] = useState(false);
  const area = getCurrentAreaInClient();
  const pathname = usePathname();
  const orpc = useORPC();
  const {
    preload,
    statuses,
    loadedUrls
  } = useImagePreloader();
  const {
    track,
    TrackingEventType
  } = useTracking();
  const {
    hasSession,
    isNewUser
  } = useUserState();
  const [advertisementRecords, setAdvertisementRecords] = useAtom(advertisementRecordsAtom);
  let t0;
  if ($[0] !== advertisementRecords) {
    t0 = [advertisementRecords];
    $[0] = advertisementRecords;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(_temp, t0);
  const t1 = orpc.cms.content.public_findLiveWithPagination.queryOptions({
    input: {
      page: 1,
      pageSize: 10,
      area: area?.toUpperCase(),
      parentAlias: "advertisement-config",
      platform: "web"
    }
  });
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      staleTime: 300000
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: cmsResponse,
    isLoading
  } = useQuery(t2);
  let t3;
  if ($[4] !== cmsResponse) {
    t3 = (cmsResponse as any)?.items?.find(_temp2);
    $[4] = cmsResponse;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const popupDataItem = t3 as AdvertisementConfig | undefined;
  const image = popupDataItem?.data?.image;
  const link = popupDataItem?.data?.link;
  const altText = popupDataItem?.data?.altText || image?.alt || "Newcomer promotion";
  const [shouldShow, setShouldShow] = useState(false);
  let t4;
  let t5;
  if ($[6] !== advertisementRecords || $[7] !== area || $[8] !== hasSession || $[9] !== isNewUser || $[10] !== pathname || $[11] !== popupDataItem) {
    t4 = () => {
      if (!popupDataItem) {
        return;
      }
      const shouldShowResult = shouldShowAdvertisementComplete(popupDataItem, "web", area, pathname, popupDataItem.data?.userType, hasSession, isNewUser, advertisementRecords);
      setShouldShow(shouldShowResult);
    };
    t5 = [popupDataItem, area, pathname, hasSession, isNewUser, advertisementRecords];
    $[6] = advertisementRecords;
    $[7] = area;
    $[8] = hasSession;
    $[9] = isNewUser;
    $[10] = pathname;
    $[11] = popupDataItem;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== image || $[15] !== preload) {
    t6 = () => {
      if (!image) {
        return;
      }
      const imageUrl = getImageUrlByDevice(image);
      if (imageUrl) {
        preload("newcomer-popup", [imageUrl]);
      }
    };
    t7 = [image, preload];
    $[14] = image;
    $[15] = preload;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  const isImageReady = statuses["newcomer-popup"] === "loaded" && !!loadedUrls["newcomer-popup"];
  let t8;
  if ($[18] !== advertisementRecords || $[19] !== area || $[20] !== popupDataItem) {
    t8 = () => {
      if (!popupDataItem) {
        return false;
      }
      return canShowBasedOnFrequency(popupDataItem, advertisementRecords, area);
    };
    $[18] = advertisementRecords;
    $[19] = area;
    $[20] = popupDataItem;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const checkFrequency = t8;
  let t9;
  if ($[22] !== checkFrequency || $[23] !== isImageReady || $[24] !== isOpen || $[25] !== shouldShow) {
    t9 = () => {
      if (shouldShow && isImageReady && !isOpen && checkFrequency()) {
        setIsOpen(true);
      }
    };
    $[22] = checkFrequency;
    $[23] = isImageReady;
    $[24] = isOpen;
    $[25] = shouldShow;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== advertisementRecords || $[28] !== area || $[29] !== isImageReady || $[30] !== isOpen || $[31] !== popupDataItem || $[32] !== shouldShow) {
    t10 = [shouldShow, isImageReady, isOpen, popupDataItem, advertisementRecords, area];
    $[27] = advertisementRecords;
    $[28] = area;
    $[29] = isImageReady;
    $[30] = isOpen;
    $[31] = popupDataItem;
    $[32] = shouldShow;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[34] !== TrackingEventType || $[35] !== isOpen || $[36] !== link || $[37] !== popupDataItem || $[38] !== track) {
    t11 = () => {
      if (isOpen && popupDataItem) {
        track("PopMartGlobalWebConfigPopupShow", {
          jump_url: link || ""
        }, TrackingEventType.VIEW);
      }
    };
    t12 = [isOpen, popupDataItem, link, track, TrackingEventType];
    $[34] = TrackingEventType;
    $[35] = isOpen;
    $[36] = link;
    $[37] = popupDataItem;
    $[38] = track;
    $[39] = t11;
    $[40] = t12;
  } else {
    t11 = $[39];
    t12 = $[40];
  }
  useEffect(t11, t12);
  let t13;
  if ($[41] !== area || $[42] !== popupDataItem || $[43] !== setAdvertisementRecords) {
    t13 = open => {
      setIsOpen(open);
      if (popupDataItem) {
        const cacheKey = `${popupDataItem.id}_${area}`;
        const contentHash = generateAdvertisementContentHash(popupDataItem);
        setAdvertisementRecords(prev => ({
          ...prev,
          [cacheKey]: {
            id: popupDataItem.id,
            area,
            lastShown: Date.now(),
            contentHash
          }
        }));
      }
    };
    $[41] = area;
    $[42] = popupDataItem;
    $[43] = setAdvertisementRecords;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const handleOpenChange = t13;
  let t14;
  if ($[45] !== TrackingEventType || $[46] !== area || $[47] !== link || $[48] !== popupDataItem || $[49] !== setAdvertisementRecords || $[50] !== track) {
    t14 = () => {
      if (popupDataItem) {
        track("PopMartGlobalWebConfigPopupClick", {
          jump_url: link || ""
        }, TrackingEventType.CLICK);
        const cacheKey_0 = `${popupDataItem.id}_${area}`;
        const contentHash_0 = generateAdvertisementContentHash(popupDataItem);
        setAdvertisementRecords(prev_0 => ({
          ...prev_0,
          [cacheKey_0]: {
            id: popupDataItem.id,
            area,
            lastShown: Date.now(),
            contentHash: contentHash_0
          }
        }));
      }
      setIsOpen(false);
    };
    $[45] = TrackingEventType;
    $[46] = area;
    $[47] = link;
    $[48] = popupDataItem;
    $[49] = setAdvertisementRecords;
    $[50] = track;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  const handleImageClick = t14;
  if (isLoading || !image || !shouldShow || !isOpen) {
    return null;
  }
  let t15;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DialogPrimitive.Overlay className={cn("fixed inset-0 z-50 bg-black/30", "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0")} />;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("fixed inset-0 z-50 flex flex-col items-center justify-center outline-none", "p-[calc(20/750*100vw)] md:p-4", "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95");
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DialogPrimitive.Title className="sr-only">Newcomer promotion</DialogPrimitive.Title>;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== altText || $[56] !== image) {
    t18 = {
      ...image,
      alt: altText
    };
    $[55] = altText;
    $[56] = image;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = cn("block object-contain", "w-[calc(650/750*100vw)] md:w-[clamp(300px,540/1920*100vw,540px)]", "h-[calc(784/750*100vw)] md:h-[clamp(300px,580/1920*100vw,580px)]");
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t18) {
    t20 = <CMSImage image={t18} width={540} height={580} className={t19} />;
    $[59] = t18;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== link || $[62] !== t20) {
    t21 = <CMSLink link={link}>{t20}</CMSLink>;
    $[61] = link;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = cn("mt-2 ring-0 outline-none");
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <DialogPrimitive.Close asChild={true}><button type="button" className={t22} aria-label="Close" onClick={_temp3}><CloseCircle className="size-7.5" /></button></DialogPrimitive.Close>;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== handleImageClick || $[67] !== t21) {
    t24 = <DialogPrimitive.Portal>{t15}<DialogPrimitive.Content className={t16}><div className="relative flex flex-col items-center" onClick={handleImageClick}>{t17}{t21}{t23}</div></DialogPrimitive.Content></DialogPrimitive.Portal>;
    $[66] = handleImageClick;
    $[67] = t21;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== handleOpenChange || $[70] !== isOpen || $[71] !== t24) {
    t25 = <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>{t24}</DialogPrimitive.Root>;
    $[69] = handleOpenChange;
    $[70] = isOpen;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  return t25;
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp2(item) {
  return item.type === "newcomer-popup" && ["web", "all"].includes(item?.platform);
}
function _temp() {}